import { Link } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { useCart } from '../lib/cart';
import IndiaCartSection from '../components/IndiaCartSection';
import { useAuth } from '../lib/auth';
import { promo as promoApi } from '../lib/api';
import { Minus, Plus, Trash2, ArrowRight, ShoppingBag, Sparkles, ShieldCheck, Video } from 'lucide-react';

export default function Cart() {
  const cart = useCart();
  const { user } = useAuth();
  const [promo, setPromo] = useState(null);

  useEffect(() => {
    let cancel = false;
    (async () => {
      if (!user || cart.items.length === 0) { setPromo(null); return; }
      try {
        const r = await promoApi.firstOrder(cart.totals.subtotal);
        if (!cancel) setPromo(r);
      } catch (e) { console.warn('[cart] promo lookup skipped', e?.response?.status); }
    })();
    return () => { cancel = true; };
  }, [user?.id, cart.totals.subtotal, cart.items.length]);

  if (cart.items.length === 0 && cart.india.length > 0) {
    return <div className="max-w-5xl mx-auto px-4 md:px-6 py-8"><IndiaCartSection/></div>;
  }

  if (cart.items.length === 0) {
    return (
      <div className="max-w-3xl mx-auto px-6 py-20 text-center">
        <ShoppingBag className="w-12 h-12 mx-auto text-ink/30"/>
        <h1 className="font-heading font-bold text-3xl mt-4">Your cart is empty</h1>
        <p className="text-ink/60 mt-2">Browse local stores and add some essentials.</p>
        <Link to="/stores" data-testid="browse-stores-link" className="btn-primary mt-6 inline-flex">Browse stores <ArrowRight className="w-4 h-4"/></Link>
      </div>
    );
  }

  const promoDiscount = (promo?.eligible ? promo.discount : 0) || 0;
  const afterPromo = Math.max(0, cart.totals.subtotal + cart.totals.delivery - promoDiscount);

  // Detect high-value / costly items in cart for advisory tags
  const hasHighValueItem = cart.items.some(i => Number(i.product.price) >= 10000);
  const otpHint = cart.totals.subtotal >= 2000 || hasHighValueItem;
  const videoHint = cart.totals.subtotal >= 5000;

  return (
    <div className="max-w-5xl mx-auto px-4 md:px-6 py-10">
      {cart.india.length > 0 && <div className="mb-8"><IndiaCartSection/></div>}
      <h1 className="font-heading font-black text-3xl md:text-4xl">{cart.india.length > 0 ? 'Local shop cart' : 'Your cart'}</h1>
      <div className="text-sm text-ink/60 mt-1">From <b>{cart.store.name}</b></div>

      <div className="mt-6 grid lg:grid-cols-[1fr_360px] gap-8">
        <div className="space-y-3">
          {cart.items.map(({ product, qty }) => (
            <div key={product.id} data-testid={`cart-row-${product.id}`} className="card-soft p-3 md:p-4 flex flex-wrap sm:flex-nowrap items-center gap-3 md:gap-4">
              <div className="w-14 h-14 md:w-16 md:h-16 rounded-lg bg-muted overflow-hidden shrink-0">{product.image_url && <img src={product.image_url} alt={product.name} className="w-full h-full object-cover"/>}</div>
              <div className="flex-1 min-w-[120px]">
                <div className="font-heading font-semibold text-ink line-clamp-1">{product.name}</div>
                <div className="text-xs text-muted-foreground">₹{product.price} / {product.unit}</div>
              </div>
              <div className="inline-flex items-center gap-2 bg-white border border-border rounded-full p-1">
                <button data-testid={`cart-dec-${product.id}`} onClick={() => cart.setQty(product.id, qty - 1)} className="w-7 h-7 grid place-items-center rounded-full hover:bg-muted"><Minus className="w-3.5 h-3.5"/></button>
                <span className="text-sm font-semibold min-w-[18px] text-center">{qty}</span>
                <button data-testid={`cart-inc-${product.id}`} onClick={() => cart.setQty(product.id, qty + 1)} className="w-7 h-7 grid place-items-center rounded-full hover:bg-muted"><Plus className="w-3.5 h-3.5"/></button>
              </div>
              <div className="font-bold ml-auto sm:ml-0 sm:w-20 text-right">₹{(product.price * qty).toFixed(2)}</div>
              <button data-testid={`cart-remove-${product.id}`} onClick={() => cart.setQty(product.id, 0)} className="text-ink/40 hover:text-destructive"><Trash2 className="w-4 h-4"/></button>
            </div>
          ))}
        </div>

        <aside className="card-soft p-5 h-fit lg:sticky lg:top-24">
          <div className="font-heading font-bold text-lg">Order summary</div>

          {promo && (
            <div data-testid="cart-promo-banner" className={`mt-3 rounded-lg p-3 text-xs flex items-start gap-2 border ${promo.eligible ? 'bg-[#FFF4E6] border-[#F2A157] text-[#8E3B00]' : 'bg-[#F6F4EE] border-border text-ink/70'}`}>
              <Sparkles className="w-4 h-4 mt-0.5 shrink-0 text-[#E67E22]"/>
              <div>
                {promo.eligible
                  ? <><b>WELCOME100 applied!</b> ₹{promo.discount.toFixed(0)} off your first order — paid by BharatCart.</>
                  : (!promo.is_first_order
                      ? <><b>Welcome promo unavailable</b> — applies only to a first-ever BharatCart order.</>
                      : <><b>Unlock ₹100 off!</b> {promo.reason}</>)
                }
              </div>
            </div>
          )}

          <div className="mt-3 space-y-2 text-sm">
            <div className="flex justify-between"><span>Subtotal</span><span data-testid="summary-subtotal">₹{cart.totals.subtotal.toFixed(2)}</span></div>
            <div className="flex justify-between"><span>Delivery</span><span data-testid="summary-delivery">₹{cart.totals.delivery.toFixed(2)}</span></div>
            {promoDiscount > 0 && (
              <div className="flex justify-between text-[#1B5E20]"><span>WELCOME100 discount</span><span data-testid="summary-promo">– ₹{promoDiscount.toFixed(2)}</span></div>
            )}
            <div className="h-px bg-border my-2"/>
            <div className="flex justify-between font-bold text-lg"><span>Total</span><span data-testid="summary-total">₹{afterPromo.toFixed(2)}</span></div>
          </div>

          {(otpHint || videoHint) && (
            <div className="mt-3 space-y-1.5 text-[11px]">
              {otpHint && (
                <div data-testid="cart-otp-hint" className="rounded-lg bg-[#E8F5E9] border border-[#A5D6A7] text-[#1B5E20] px-2.5 py-2 flex items-start gap-1.5">
                  <ShieldCheck className="w-3.5 h-3.5 mt-0.5 shrink-0"/>
                  <span>OTP will be required at delivery for this order (important goods / high value).</span>
                </div>
              )}
              {videoHint && (
                <div data-testid="cart-video-hint" className="rounded-lg bg-[#FFF8E1] border border-[#FFD180] text-[#7A4A00] px-2.5 py-2 flex items-start gap-1.5">
                  <Video className="w-3.5 h-3.5 mt-0.5 shrink-0"/>
                  <span>Returns on this order will need a short video proof (costly goods).</span>
                </div>
              )}
            </div>
          )}

          {cart.totals.subtotal < (cart.store?.min_order || 0) && (
            <div className="mt-3 text-xs text-[#9A4500] bg-[#FFF3E0] border border-[#FFCC80] rounded-lg p-3">Add ₹{((cart.store?.min_order || 0) - cart.totals.subtotal).toFixed(2)} more to reach minimum order.</div>
          )}
          <Link to="/checkout" data-testid="proceed-to-checkout" className={`btn-primary w-full mt-5 ${cart.totals.subtotal < (cart.store?.min_order || 0) ? 'opacity-50 pointer-events-none' : ''}`}>Proceed to checkout <ArrowRight className="w-4 h-4"/></Link>
        </aside>
      </div>
    </div>
  );
}
